'use client'

import React, { ReactNode, createContext, useContext, useState, useCallback } from 'react'
import { ProviderProps } from '../types/types'
import { requestHandler } from '../utils/requestHandler'
import { useToast } from './toast-provider'
import {
    createJobDescriptionRequest,
    deleteJobDescriptionRequest,
    getAllJobDescriptionsRequest,
    getOneJobDescriptionRequest,
    updateJobDescriptionRequest,
} from '../utils/handlers/client/jobDescription'

export const dynamic = 'force-dynamic'

type JobDescription = {
    id: string
    title: string
    content: string
    notes?: string
    status: string
    companyId: number
    createdAt: string
    updatedAt: string
    createdBy?: string
    linkedJobCount?: number
}

type JobDescriptionMapType = {
    [key: string]: JobDescription | null
}

type JobDescriptionCacheEntry = {
    ids: string[]
    map: JobDescriptionMapType
    meta: { [key: string]: any }
}

// Helper function to build cache key from query
const buildCacheKey = (query: { [key: string]: any }) => {
    const normalizedEntries = Object.entries(query || {})
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    return JSON.stringify(normalizedEntries)
}

// Create a context to manage JobDescriptions-related data and functions
const JobDescriptionContext = createContext({
    error: '',
    map: {} as JobDescriptionMapType,
    list: [] as string[],
    setList: (list: string[]) => {},
    setMap: (map: JobDescriptionMapType) => {},
    metaData: {} as { [key: string]: any },
    createJobDescription: async (data: any, setIsLoading: (loading: boolean) => void, isNotify = true) => null as any,
    getAllJobDescriptions: async (query: { [key: string]: any }, setIsLoading: (loading: boolean) => void, options?: { skipCache?: boolean }) => {},
    updateJobDescription: async (id: string, data: any, setIsLoading: (loading: boolean) => void) => {},
    deleteJobDescription: async (id: string, data: any, setIsLoading: (loading: boolean) => void) => {},
    getOneJobDescription: async (id: string, data: any, setIsLoading: (loading: boolean) => void): Promise<any> => null as any,
    clearCache: () => {},
})

// Create a hook to access the JobDescriptionContext
const useJobDescriptionContext = () => useContext(JobDescriptionContext)

// Create a component
const JobDescriptionProvider: React.FC<ProviderProps> = ({ children }) => {
    const { showErrorToast, showSuccessToast } = useToast()
    const [map, setMap] = useState<JobDescriptionMapType>({})
    const [list, setList] = useState<string[]>([])
    const [metaData, setMetaData] = useState<{ [key: string]: any }>({})
    const [error, setError] = useState('')
    const [pageCache, setPageCache] = useState<Record<string, JobDescriptionCacheEntry>>({})

    // Clear cache function
    const clearCache = useCallback(() => {
        setPageCache({})
    }, [])

    // Function to handle create JobDescription
    const createJobDescription = async (data: any, setIsLoading: (loading: boolean) => void, isNotify = true) => {
        console.log('createJobDescription called with data:', data)
        let createdJobDescription: JobDescription | null = null
        setError('')

        try {
            await requestHandler(
                async () => {
                    console.log('Making API call to createJobDescriptionRequest')
                    return await createJobDescriptionRequest(data)
                },
                (res: any) => {
                    // requestHandler already extracts response.data, so res = { success, data, message }
                    const { data, message } = res
                    const jobDescriptionId = data.id!
                    createdJobDescription = data

                    console.log('Updating context with new JD:', { jobDescriptionId, data })

                    setMap((prevMap) => ({
                        ...prevMap,
                        [jobDescriptionId]: data,
                    }))
                    setList((prevList) => [String(jobDescriptionId), ...prevList])
                    // Clear cache since new JD affects all queries
                    setPageCache({})
                    isNotify && showSuccessToast(message || 'Job description created successfully')
                },
                (errMessage: any) => {
                    console.error('API error in createJobDescription:', errMessage)
                    // Handle both direct error message and wrapped error format
                    const errorMessage = typeof errMessage === 'string' ? errMessage : (errMessage as any)?.error || 'Failed to create job description'
                    isNotify && showErrorToast(errorMessage)
                    setError(errorMessage)
                    throw new Error(errorMessage)
                },
                setIsLoading
            )
        } catch (error) {
            console.error('Error in createJobDescription:', error)
            throw error
        }

        console.log('createJobDescription returning:', createdJobDescription)
        return createdJobDescription
    }

    // Function to handle get all JobDescriptions with caching
    const getAllJobDescriptions = useCallback(
        async (query: { [key: string]: any }, setIsLoading: (loading: boolean) => void, options?: { skipCache?: boolean }) => {
            const cacheKey = buildCacheKey(query)
            const cachedEntry = pageCache[cacheKey]

            // Return cached data if available (unless skipCache is true)
            if (cachedEntry && !options?.skipCache) {
                setMap((prevMap) => ({ ...prevMap, ...cachedEntry.map }))
                setList(cachedEntry.ids)
                setMetaData((prevMeta) => ({
                    ...prevMeta,
                    ...cachedEntry.meta,
                }))
                // Cache path skips requestHandler, so clear loading here —
                // otherwise remounts that start with loading=true stay stuck.
                setIsLoading(false)
                return
            }

            await requestHandler(
                async () => await getAllJobDescriptionsRequest(query),
                (res: any) => {
                    const { data } = res
                    const { jds = [], ...metaData } = data as {
                        jds: JobDescription[]
                        [key: string]: any
                    }

                    const jobDescriptionMap = jds.reduce((map: JobDescriptionMapType, jd: JobDescription) => {
                        map[jd?.id!] = jd
                        return map
                    }, {})
                    // Preserve API order (createdAt desc) for list display
                    const jobDescriptionIds = jds.map((jd) => String(jd?.id!)).filter(Boolean)

                    // Update cache
                    setPageCache((prevCache) => ({
                        ...prevCache,
                        [cacheKey]: {
                            ids: jobDescriptionIds,
                            map: jobDescriptionMap,
                            meta: metaData,
                        },
                    }))

                    setMap((prevMap) => ({ ...prevMap, ...jobDescriptionMap }))
                    setList(jobDescriptionIds)
                    setMetaData(metaData)
                },
                (errorMsg) => {
                    console.log(errorMsg)
                    showErrorToast(errorMsg)
                },
                setIsLoading
            )
        },
        [pageCache, showErrorToast]
    )

    const updateJobDescription = async (id: string, data: any, setIsLoading: (loading: boolean) => void) => {
        await requestHandler(
            async () => await updateJobDescriptionRequest(id, data),
            (res: any) => {
                const { data, message } = res
                const jobDescriptionId = data.id!
                setMap((prevMap) => ({
                    ...prevMap,
                    [jobDescriptionId]: data,
                }))
                // Clear cache since JD update might affect filtering
                setPageCache({})
                showSuccessToast(message || 'Job description updated successfully')
            },
            (errMessage) => {
                showErrorToast(errMessage)
                throw new Error(errMessage)
            },
            setIsLoading
        )
    }

    const deleteJobDescription = async (id: string, data: any, setIsLoading: (loading: boolean) => void) => {
        await requestHandler(
            async () => await deleteJobDescriptionRequest(id, data),
            (res: any) => {
                const { message } = res
                // Use the passed-in `id` directly — the DELETE response may not include data.id
                setMap((prevMap) => {
                    const newMap = { ...prevMap }
                    delete newMap[id]
                    return newMap
                })
                setList((prevList) => prevList.filter((item) => item !== id))
                // Clear cache since deletion affects all queries
                setPageCache({})
                showSuccessToast(message || 'Job description deleted successfully')
            },
            (errMessage) => {
                showErrorToast(errMessage)
            },
            setIsLoading
        )
    }

    const getOneJobDescription = async (id: string, data: any, setIsLoading: (loading: boolean) => void) => {
        let result: any = null
        await requestHandler(
            async () => await getOneJobDescriptionRequest(id, data),
            (res: any) => {
                const { data, message } = res
                const jobDescriptionId = data.id!
                setMap((prevMap) => ({
                    ...prevMap,
                    [jobDescriptionId]: data,
                }))
                result = data
            },
            (errMessage) => {
                showErrorToast(errMessage)
            },
            setIsLoading
        )
        return result
    }

    // Provide JobDescriptions-related data and functions through the context
    return (
        <JobDescriptionContext.Provider
            value={{
                error,
                map,
                list,
                setList,
                setMap,
                metaData,
                createJobDescription,
                getAllJobDescriptions,
                updateJobDescription,
                deleteJobDescription,
                getOneJobDescription,
                clearCache,
            }}
        >
            {children}
        </JobDescriptionContext.Provider>
    )
}

// Export the context, provider component, and custom hook
export { JobDescriptionContext, JobDescriptionProvider, useJobDescriptionContext }
